import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { LucideIcon } from "lucide-react-native";
import React, { ComponentProps } from "react";

export type KpiTone =
  | "brand"
  | "info"
  | "pink"
  | "success"
  | "warning"
  | "teal";

const toneClass: Record<KpiTone, { bg: string; color: string }> = {
  brand: { bg: "bg-brand-50", color: "text-brand-500" },
  info: { bg: "bg-info-50", color: "text-info-600" },
  pink: { bg: "bg-error-50", color: "text-error-500" },
  success: { bg: "bg-success-50", color: "text-success-700" },
  warning: { bg: "bg-warning-50", color: "text-warning-500" },
  teal: { bg: "bg-info-50", color: "text-info-500" },
};

type Props = {
  icon?: LucideIcon;
  image?: ComponentProps<typeof Image>["source"];
  tone: KpiTone;
  label: string;
  value: React.ReactNode;
  unit?: string;
  sub: string;
};

const KpiCard = React.memo(
  ({ icon, image, tone, label, value, unit, sub }: Props) => {
    const t = toneClass[tone];
    return (
      <Card variant="outline" className="rounded-2xl h-full" size="md">
        <VStack space="sm">
          <HStack className="items-center justify-between">
            {image ? (
              <Image
                source={image}
                alt={label}
                size="none"
                className="w-12 h-12"
                contentFit="contain"
              />
            ) : icon ? (
              <Box
                className={`${t.bg} w-8 h-8 rounded-lg items-center justify-center`}
              >
                <Icon as={icon} className={t.color} size="sm" />
              </Box>
            ) : null}
          </HStack>
          <Text size="xs" className="text-typography-500 font-medium">
            {label}
          </Text>
          <HStack space="xs" className="items-baseline">
            <Text size="2xl" className="text-typography-900 font-bold">
              {value}
            </Text>
            {unit ? (
              <Text size="sm" className="text-typography-500">
                {unit}
              </Text>
            ) : null}
          </HStack>
          <Text size="xs" className="text-typography-500">
            {sub}
          </Text>
        </VStack>
      </Card>
    );
  },
);

KpiCard.displayName = "KpiCard";

export default KpiCard;
